import { AppConfigsService } from '../../../../../config/app-configs.service';
import { Injectable } from '@angular/core';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx/types';
import { DebitListService } from './main-debit-list.service';
import { Currency, ITransactionExcel } from '../interfaces/IExcel.interface';
import { HokHeaderItem } from '../../../interfaces/IGetHokDetailsForAllCard.interface';
import { EHokStatus, IFilterState } from '../interfaces/filters.interface';

const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
const EXCEL_EXTENSION = '.xlsx';

@Injectable()
export class ExcelService {
    private headers: string[] = [];

    constructor(public appConfigsService: AppConfigsService, private debitListService: DebitListService) {
        this.initTableHeaders();
    }

    exportAsExcelFile(
        hoks: HokHeaderItem[],
        filterState: IFilterState,
        excelFileName: string,
        userLength?: number,
        cardLength?: number
    ): void {
        // tslint:disable-next-line:no-shadowed-variable
        import('xlsx').then((XLSX) => {
            const workbook = {
                Workbook: {
                    Views: [{ RTL: true }]
                },
                Sheets: {},
                SheetNames: []
            };
            const extraDataCount = 3;
            let extraDataCounter = 0;

            const first = 4;

            if (hoks && hoks.length > 0) {
                extraDataCounter = 0;
                const transactionsExcel: ITransactionExcel[] = [];

                // tslint:disable-next-line:variable-name
                const __this = this;

                // tslint:disable-next-line:typedef
                hoks.forEach(function (hok: HokHeaderItem) {
                    const newObj: ITransactionExcel = {
                        merchantName: hok.merchantName,
                        sourceCard: hok.sourceCard,
                        firstPurchaseDate: __this.getDate(hok.firstPurchaseDate),
                        lastDebitDate: __this.getDate(hok.lastDebitDate),
                        amount: hok.amount,
                        curency: __this.getCurrencySymbol(Currency.ILS),
                        status: __this.getStatusName(hok)
                    };
                    transactionsExcel.push(newObj);
                });

                const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet([], {
                    skipHeader: true,
                    header: ['']
                });

                extraDataCounter++;
                let usersText = '';

                if (filterState.selectedUser) {
                    usersText = filterState.selectedUser.name;
                } else {
                    usersText = 'כל המשתמשים' + ' (' + userLength + ')';
                }

                XLSX.utils.sheet_add_aoa(worksheet, [[usersText]], {
                    origin: 'A' + extraDataCounter
                });

                extraDataCounter++;
                let cardsText = '';

                if (filterState.bankAccount) {
                    filterState.selectedCard.forEach(element => {
                        cardsText += element.cardName + '-' + element.shortCardNumber + ' ';
                    });
                } else if (filterState.selectedCard) {
                    cardsText = filterState.selectedCard[0].cardName + '-' + filterState.selectedCard[0].shortCardNumber;
                } else {
                    cardsText = 'כל הכרטיסים' + '(' + cardLength + ')';
                }

                XLSX.utils.sheet_add_aoa(worksheet, [[cardsText]], { origin: 'A' + extraDataCounter });

                extraDataCounter++;
                const statusText =
                    'סטטוס' + '(' + __this.debitListService.getStatusName(filterState.selectedStatus) + ')';

                XLSX.utils.sheet_add_aoa(worksheet, [[statusText]], { origin: 'A' + extraDataCounter });

                extraDataCounter += 2;

                XLSX.utils.sheet_add_aoa(worksheet, [this.headers], { origin: 'A' + extraDataCounter });
                extraDataCounter++;

                XLSX.utils.sheet_add_json(worksheet, transactionsExcel, {
                    skipHeader: true,
                    header: [
                        'merchantName',
                        'sourceCard',
                        'firstPurchaseDate',
                        'lastDebitDate',
                        'amount',
                        'curency',
                        'status'
                    ],
                    origin: 'A' + extraDataCounter
                });

                // tslint:disable-next-line:max-line-length
                // let worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(table.transactions, { header: this.headers, skipHeader: false });
                workbook.SheetNames.push('הוראות קבע');
                workbook.Sheets['הוראות קבע'] = worksheet;
            }

            const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
            this.saveAsExcelFile(excelBuffer, excelFileName);
        });
    }
    private getStatusName(hok: HokHeaderItem): string {
        if (hok.messageStatus === '1' || hok.messageStatus === '2') {
            return `${this.debitListService.getStatusName(EHokStatus.Transference)}  לכרטיס ${hok.destinyCard}`;
        }
        if (hok.status === 2) {
            return this.debitListService.getStatusName(EHokStatus.Canceled);
        }
        if (hok.status === 1) {
            return this.debitListService.getStatusName(EHokStatus.Active);
        }
        if (hok.status === 0) {
            return this.debitListService.getStatusName(EHokStatus.Off);
        }
    }
    private getTransactionType(dealData: any): string {
        const id: number = dealData.tdmTransactionType as number;

        if (id === 99 || id === 4) {
            return 'אחר';
        } else if (id === 1 || id === 2 || id === 3 || id === 5) {
            return 'בנוכחות כרטיס';
        } else if (id === 7 || id === 8) {
            return 'טלפוני';
        } else if (id === 9 || id === 10) {
            return 'אינטרנט';
        } else if (id === 11 || id === 12) {
            return dealData.CardToken !== null ? 'אינטרנט' : 'עסקת contactless';
        } else if (id === 6) {
            return 'עסקה חכמה';
        } else {
            return '';
        }
    }

    private getCurrencySymbol(currency: any): string {
        if (currency !== undefined) {
            switch (currency as Currency) {
                case Currency.ILS:
                    return '₪';
                case Currency.EUR:
                    return '€';
                case Currency.USD:
                    return '$';
                default:
                    return '';
            }
        }

        return '';
    }

    private getDate(date: any): any {
        if (date) {
            const day = date.substr(8, 2);
            const month = date.substr(5, 2);
            const year = date.substr(0, 4);
            return day + '-' + month + '-' + year;
        }
        return null;
    }

    private saveAsExcelFile(buffer: any, fileName: string): void {
        const data: Blob = new Blob([buffer], {
            type: EXCEL_TYPE
        });
        FileSaver.saveAs(data, fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION);
    }

    private initTableHeaders(): void {
        this.headers.push('בית העסק');
        this.headers.push('כרטיס');
        this.headers.push('תאריך ביצוע');
        this.headers.push('חיוב אחרון');
        this.headers.push('סכום החיוב');
        this.headers.push('מטבע חיוב');
        this.headers.push('סטטוס');
    }
}
